/**
 * Módulo clientes: listado, alta rápida (reutilizable desde el POS), ficha con crédito (admin+) y estado de cuenta. La
 * ficha lleva los archivos adjuntos del cliente (pedido de la dueña 2026-10-19).
 */
import { clienteCrearInput, METODO_PAGO_ETIQUETA, Money, PLAZO_CREDITO_NORMAL, type ClienteCrearEntrada, type ClienteCrearInput, type ClienteDTO, type MetodoPago, type MovimientoSaldoDTO } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock, Plus, Search, UserPlus, Users, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Adjuntos } from '@/componentes/Adjuntos.tsx';
import { CorreosEnviados } from '@/componentes/CorreosEnviados.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo, Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { ImportarExcel } from '@/componentes/ImportarExcel.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { ErrorApi, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { veParteDivisa } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useListasPrecio } from '@/modulos/productos/api.ts';
import { useClientes, useCrearCliente, useEditarClienteBasico, useEditarClienteCredito, useEstadoCuenta } from './api.ts';
import { CampoRif } from './CampoRif.tsx';
import { DialogoAjustarSaldo, DialogoTraspasarSaldo } from './SaldoAcciones.tsx';
import { EnviarCorreo } from '@/componentes/EnviarCorreo.tsx';
import { normalizarDecimal } from '@/lib/montos.ts';

export function EtiquetasCliente({ c }: { c: ClienteDTO }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {c.consumidor_final ? <Badge variant="neutro">Consumidor final</Badge> : null}
      {c.tipo_contribuyente === 'especial' ? <Badge variant="fiscal">Especial</Badge> : null}
      {c.municipal ? <Badge variant="info">Municipal {c.retencion_municipal_pct ? `${Money.parse(c.retencion_municipal_pct).toString()}%` : '(global)'}</Badge> : null}
      {c.condicion === 'credito' ? <Badge variant="pendiente">Crédito {c.plazo_dias} d</Badge> : null}
      {!c.activo ? <Badge variant="critico">Inactivo</Badge> : null}
    </span>
  );
}

/** Alta rápida (POS-05): datos fiscales mínimos. Devuelve el cliente creado al padre. */
export function DialogClienteRapido({ abierto, cerrar, alCrear, cliente }: { abierto: boolean; cerrar: () => void; alCrear?: (c: ClienteDTO) => void; cliente?: ClienteDTO }) {
  const crear = useCrearCliente();
  const editar = useEditarClienteBasico(cliente?.id ?? '');
  const navigate = useNavigate();
  // El % municipal propio lo fija administración; los demás marcan la bandera (se usa el % general y se avisa al admin)
  const puedeClasificarPct = useAuth().puede('clientes.credito');
  // Alta: datos básicos + clasificación fiscal inicial (especial / municipal). Edición: solo datos básicos (la clasificación la cambia admin en la ficha)
  const form = useForm<ClienteCrearEntrada, unknown, ClienteCrearInput>({
    resolver: zodResolver(clienteCrearInput),
    ...(cliente
      ? { values: { rif: cliente.rif, razon_social: cliente.razon_social, direccion_fiscal: cliente.direccion_fiscal, telefono: cliente.telefono ?? '', correo: cliente.correo, notas: cliente.notas, tipo_contribuyente: cliente.tipo_contribuyente, municipal: cliente.municipal, retencion_municipal_pct: cliente.retencion_municipal_pct } }
      : {}),
    defaultValues: { rif: '', razon_social: '', direccion_fiscal: '', telefono: '', correo: null, notas: null, tipo_contribuyente: 'ordinario', municipal: false, retencion_municipal_pct: null },
  });
  const municipal = useWatch({ control: form.control, name: 'municipal' });
  const e = form.formState.errors;
  const rifActual = useWatch({ control: form.control, name: 'rif' });
  const enviar = form.handleSubmit(async (d) => {
    const datos = { ...d, correo: d.correo || null, notas: d.notas || null, retencion_municipal_pct: d.municipal && d.retencion_municipal_pct ? d.retencion_municipal_pct : null };
    try {
      if (cliente) {
        const { tipo_contribuyente: _t, municipal: _m, retencion_municipal_pct: _p, ...basico } = datos;
        await editar.mutateAsync(basico);
        toast.success('Cliente actualizado');
      } else {
        const c = await crear.mutateAsync(datos);
        toast.success(`Cliente ${c.razon_social} creado`, { description: 'Lista detal · contado (el administrador puede cambiarlo).' });
        alCrear?.(c);
      }
      form.reset();
      cerrar();
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === 'rif_duplicado') {
        const id = (err.detalles as { cliente_id?: string } | undefined)?.cliente_id;
        toast.error(err.message, { action: id ? { label: 'Ver ficha', onClick: () => navigate(`/clientes/${id}`) } : undefined });
      } else toast.error('No se pudo guardar', { description: mensajeDeError(err) });
    }
  });
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent>
        <form onSubmit={enviar} noValidate className="space-y-4" data-testid="form-cliente">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>{cliente ? `Editar ${cliente.razon_social}` : 'Nuevo cliente'}</DialogTitle><AyudaVentana tema="clientes.nuevo" /></div>
            <DialogDescription>Cédula o RIF y teléfono son obligatorios (la nota de entrega los pide); para FACTURA, además, la dirección fiscal. Nace con lista detal y contado.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-3">
            <input type="hidden" {...form.register('rif')} />
            <CampoRif valor={rifActual} onChange={(v) => form.setValue('rif', v, { shouldDirty: true, shouldValidate: form.formState.isSubmitted })} error={e.rif?.message} autoFocus className="col-span-2" />
            <Campo etiqueta="Nombre / Razón social" htmlFor="c-nombre" error={e.razon_social?.message} className="col-span-3">
              <Input id="c-nombre" {...form.register('razon_social')} />
            </Campo>
          </div>
          <Campo etiqueta="Dirección fiscal" htmlFor="c-dir" error={e.direccion_fiscal?.message}>
            <Input id="c-dir" {...form.register('direccion_fiscal')} />
          </Campo>
          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Teléfono (obligatorio)" htmlFor="c-tel" error={e.telefono?.message}>
              <Input id="c-tel" inputMode="tel" placeholder="0414-5550101" {...form.register('telefono')} data-testid="c-telefono" />
            </Campo>
            <Campo etiqueta="Correo" htmlFor="c-correo" error={e.correo?.message}>
              <Input id="c-correo" type="email" {...form.register('correo')} />
            </Campo>
          </div>
          {!cliente ? (
            <div className="grid grid-cols-3 items-end gap-3 rounded-lg border bg-muted/40 p-3" data-testid="c-clasificacion">
              <Campo etiqueta="Tipo de contribuyente" htmlFor="c-tipo">
                <Select id="c-tipo" {...form.register('tipo_contribuyente')} data-testid="c-tipo">
                  <option value="ordinario">Ordinario</option>
                  <option value="especial">Especial (retiene 75% del IVA)</option>
                </Select>
              </Campo>
              <label className="flex h-10 items-center gap-2 text-sm">
                <input type="checkbox" {...form.register('municipal')} data-testid="c-municipal" /> Contribuyente municipal
              </label>
              {municipal && puedeClasificarPct ? (
                <Campo etiqueta="% retención municipal" htmlFor="c-mun-pct" error={e.retencion_municipal_pct?.message} ayuda="Vacío = % general de Configuración">
                  <Input id="c-mun-pct" inputMode="decimal" placeholder="Ej. 1,00" {...form.register('retencion_municipal_pct', { setValueAs: (v: string) => (v === '' || v === null ? null : v) })} data-testid="c-mun-pct" />
                </Campo>
              ) : null}
            </div>
          ) : null}
          <Campo etiqueta="Notas" htmlFor="c-notas">
            <Textarea id="c-notas" rows={2} {...form.register('notas')} />
          </Campo>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrar}>Cancelar</Button>
            <Button type="submit" cargando={crear.isPending || editar.isPending} data-testid="guardar-cliente">
              {cliente ? 'Guardar' : 'Crear cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Clientes() {
  const { puede } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [condicion, setCondicion] = useState('');
  const [inactivos, setInactivos] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [nuevo, setNuevo] = useState(false);
  const lista = useClientes({ q: q || undefined, tipo: (tipo || undefined) as 'ordinario' | 'especial' | undefined, condicion: (condicion || undefined) as 'contado' | 'credito' | undefined, activo: inactivos ? 'todos' : 'true', pagina, limite: 25 });
  const puedeCrear = puede('clientes.crear');
  const [importar, setImportar] = useState(false);
  const qc = useQueryClient();
  return (
    <>
      <TituloPagina titulo="Clientes" subtitulo="Ficha, lista de precios, crédito y saldo a favor." acciones={puedeCrear ? <><Button variant="outline" onClick={() => setImportar(true)} data-testid="btn-importar-clientes"><FileSpreadsheet /> Cargar Excel</Button><Button onClick={() => setNuevo(true)} data-testid="btn-nuevo-cliente"><UserPlus /> Nuevo cliente</Button></> : undefined} />
      <ImportarExcel que="clientes" tema="clientes.excel" abierto={importar} cerrar={() => setImportar(false)} alCargar={() => void qc.invalidateQueries({ queryKey: ['clientes'] })} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, RIF o teléfono…" value={q} onChange={(e) => { setQ(e.target.value); setPagina(1); }} className="w-80 pl-8" autoFocus data-testid="buscar-cliente" />
        </div>
        {/* Cada filtro con su rótulo (auditoría de frontend FE-15) */}
        <Filtro etiqueta="Contribuyente">
          <Select value={tipo} onChange={(e) => { setTipo(e.target.value); setPagina(1); }} className="w-40" aria-label="Tipo de contribuyente" data-testid="filtro-cliente-tipo">
            <option value="">Todos</option>
            <option value="ordinario">Ordinarios</option>
            <option value="especial">Especiales</option>
          </Select>
        </Filtro>
        <Filtro etiqueta="Condición">
          <Select value={condicion} onChange={(e) => { setCondicion(e.target.value); setPagina(1); }} className="w-44" aria-label="Condición de pago" data-testid="filtro-cliente-condicion">
            <option value="">Contado y crédito</option>
            <option value="contado">Contado</option>
            <option value="credito">Crédito</option>
          </Select>
        </Filtro>
        <label className="flex h-10 items-center gap-1.5 text-sm">
          <input type="checkbox" checked={inactivos} onChange={(e) => { setInactivos(e.target.checked); setPagina(1); }} /> Incluir inactivos
        </label>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio icono={<Users className="h-6 w-6" />} titulo="Sin clientes" descripcion={q ? `Nada coincide con "${q}".` : 'Registra el primer cliente.'} accion={puedeCrear ? <Button onClick={() => setNuevo(true)}><Plus /> Nuevo cliente</Button> : undefined} />
          ) : (
            <div className="space-y-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RIF</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Lista</TableHead>
                    <TableHead>Clasificación</TableHead>
                    <TableHead className="text-right">Límite crédito</TableHead>
                    <TableHead className="text-right">Saldo a favor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)} data-testid={`cliente-${c.rif}`}>
                      <TableCell className="font-mono text-xs">{c.rif}</TableCell>
                      <TableCell>
                        <p className="font-medium">{c.razon_social}</p>
                        <p className="text-[11px] text-muted-foreground">{[c.telefono, c.correo].filter(Boolean).join(' · ')}</p>
                      </TableCell>
                      <TableCell>{c.lista_nombre}</TableCell>
                      <TableCell><EtiquetasCliente c={c} /></TableCell>
                      <TableCell className="text-right">{c.condicion === 'credito' ? <Dinero valor={c.limite_credito_usd} modo="usd" /> : <span className="text-muted-foreground">contado</span>}</TableCell>
                      <TableCell className="text-right">{Money.parse(c.saldo_a_favor).esPositivo() ? <Dinero valor={c.saldo_a_favor} modo="usd" className="text-mg-verde" /> : <span className="text-muted-foreground">—</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{d.total} clientes</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                  <Button size="sm" variant="outline" disabled={pagina * d.limite >= d.total} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
                </div>
              </div>
            </div>
          )
        }
      </EstadoQuery>
      <DialogClienteRapido abierto={nuevo} cerrar={() => setNuevo(false)} alCrear={(c) => navigate(`/clientes/${c.id}`)} />
    </>
  );
}

/** Nombres a la vista de los movimientos del saldo a favor (re-auditoría 2026-10-18, ronda 2, hallazgo 1-H15). */
const TIPO_MOVIMIENTO_SALDO: Record<MovimientoSaldoDTO['tipo'], string> = { abono: 'Abono', devolucion: 'Devolución', reintegro: 'Reintegro de retención', uso_pago: 'Usado para pagar', traspaso_salida: 'Traspasado a otro cliente', traspaso_entrada: 'Traspaso recibido', ajuste: 'Ajuste (baja)' };
/** Los que bajan el saldo: se muestran en gris, los que lo suben en verde. */
const BAJA_SALDO = new Set<MovimientoSaldoDTO['tipo']>(['uso_pago', 'traspaso_salida', 'ajuste']);
const REFERENCIA_SALDO: Record<string, string> = { ABONO: 'Recibo de abono', RET: 'Retención', FAC: 'Factura', NOTA: 'Nota', NC: 'Nota de crédito', ND: 'Nota de débito', CAM: 'Cambio' };
function referenciaSaldo(m: MovimientoSaldoDTO): string {
  const d = (m.detalle ?? {}) as { recibo?: string | null; numero?: string | null; documento?: string | null };
  const numero = d.recibo ?? d.numero ?? m.documento_numero ?? (m.referencia_tipo === 'RET' && d.documento ? `de ${d.documento}` : null);
  const contraparte = m.contraparte ? `${m.tipo === 'traspaso_salida' ? 'a' : 'de'} ${m.contraparte.razon_social}` : null;
  return [REFERENCIA_SALDO[m.referencia_tipo] ?? m.referencia_tipo, numero, contraparte ? `· ${contraparte}` : null].filter(Boolean).join(' ');
}

export function ClienteFicha() {
  const { id } = useParams<{ id: string }>();
  const { puede, usuario } = useAuth();
  const navigate = useNavigate();
  const ec = useEstadoCuenta(id ?? null);
  // El expediente lista los documentos de todas las sucursales, pero quien trabaja en una (o la dueña mientras la opera)
  // solo puede abrir los de esa: los demás se muestran sin enlace, para no llevar a un «no existe» (FE-02)
  const miSucursal = usuario?.sucursal_nombre ?? null;
  const abre = (sucursal: string) => !miSucursal || sucursal === miSucursal;
  const enlaceDocumento = (docId: string, texto: string, sucursal: string) =>
    abre(sucursal) ? (
      <Link to={`/documentos/${docId}`} className="font-medium underline">{texto}</Link>
    ) : (
      <span className="font-medium" title={`Documento de ${sucursal}: se abre operando esa sucursal`} data-testid="doc-otra-sucursal">{texto}</span>
    );
  const listas = useListasPrecio();
  const editarCredito = useEditarClienteCredito(id ?? '');
  const [editando, setEditando] = useState(false);
  const [credito, setCredito] = useState<{ lista_id: string; tipo: 'ordinario' | 'especial'; municipal: boolean; pct: string; condicion: 'contado' | 'credito'; limite: string; plazo: string } | null>(null);
  const puedeCredito = puede('clientes.credito');
  const puedeEditar = puede('clientes.crear');
  // La parte del saldo en divisas sin IGTF: solo la dueña y el contador (hallazgo T20; la API se la da en null a los demás)
  const veParte = veParteDivisa(usuario);
  // Traspaso o ajuste del saldo (spec 03, enmienda 2026-10-29): permiso de la dueña o su autorización
  const [operacion, setOperacion] = useState<'traspaso' | 'ajuste' | null>(null);

  const guardarCredito = async () => {
    if (!credito) return;
    try {
      await editarCredito.mutateAsync({
        // Solo si cambió: la lista actual puede estar desactivada y el servidor no acepta guardar una lista inactiva
        // (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H3)
        ...(credito.lista_id !== ec.data?.cliente.lista_id ? { lista_id: credito.lista_id } : {}),
        tipo_contribuyente: credito.tipo,
        municipal: credito.municipal,
        retencion_municipal_pct: credito.municipal && credito.pct ? normalizarDecimal(credito.pct) : null,
        condicion: credito.condicion,
        limite_credito_usd: credito.condicion === 'credito' ? normalizarDecimal(credito.limite) : '0',
        plazo_dias: credito.condicion === 'credito' ? Number(credito.plazo) : 0,
      });
      toast.success('Clasificación y crédito actualizados', { description: 'Queda auditado con tu usuario.' });
      setCredito(null);
    } catch (e) {
      toast.error('No se pudo guardar', { description: mensajeDeError(e) });
    }
  };

  return (
    <EstadoQuery cargando={ec.isLoading} error={ec.error} datos={ec.data} reintentar={() => void ec.refetch()}>
      {({ cliente: c, movimientos_saldo, cxc, credito_disponible, documentos, cotizaciones, abonos, pagos }) => (
        <>
          {operacion === 'traspaso' ? <DialogoTraspasarSaldo cliente={c} cerrar={() => setOperacion(null)} /> : null}
          {operacion === 'ajuste' ? <DialogoAjustarSaldo cliente={c} documentos={documentos} cerrar={() => setOperacion(null)} /> : null}
          <TituloPagina
            titulo={c.razon_social}
            subtitulo={`${c.rif} · ${[c.direccion_fiscal, c.telefono, c.correo].filter(Boolean).join(' · ')}`}
            acciones={
              <>
                <Button variant="outline" onClick={() => navigate('/clientes')}>Volver</Button>
                {puedeEditar && !c.consumidor_final ? <Button variant="outline" onClick={() => setEditando(true)} data-testid="btn-editar-cliente">Editar datos</Button> : null}
                {puedeCredito && !c.consumidor_final ? (
                  <Button onClick={() => setCredito({ lista_id: c.lista_id, tipo: c.tipo_contribuyente, municipal: c.municipal, pct: c.retencion_municipal_pct ?? '', condicion: c.condicion, limite: c.limite_credito_usd, plazo: String(c.plazo_dias || PLAZO_CREDITO_NORMAL) })} data-testid="btn-credito">
                    Lista y crédito
                  </Button>
                ) : null}
                {/* Desactivar o reactivar (la API ya lo aceptaba y bloquea la venta a un inactivo; hallazgo E11) */}
                {puedeCredito && !c.consumidor_final ? (
                  <BotonActivo
                    activo={c.activo}
                    titulo={`Desactivar a ${c.razon_social}`}
                    consecuencia="No se le puede vender ni cotizar hasta activarlo de nuevo. Su ficha, su saldo a favor, su crédito y sus documentos quedan igual."
                    variante="outline"
                    size="default"
                    cargando={editarCredito.isPending}
                    alCambiar={(activo) => editarCredito.mutateAsync({ activo }).then(() => { toast.success(activo ? 'Cliente activado' : 'Cliente desactivado', { description: activo ? 'Queda auditado con tu usuario.' : 'No se le puede vender hasta activarlo de nuevo. Queda auditado con tu usuario.' }); }, (e) => { toast.error('No se pudo cambiar', { description: mensajeDeError(e) }); })}
                    testid="btn-activo-cliente"
                  />
                ) : null}
              </>
            }
          />
          <div className="mb-3">
            <EtiquetasCliente c={c} /> {!c.apto_factura && !c.consumidor_final ? <Badge variant="pendiente">Falta dirección fiscal para FACTURA</Badge> : null}{' '}
            {!c.apto_nota && !c.consumidor_final ? <Badge variant="pendiente" data-testid="falta-telefono">Falta el teléfono para la NOTA</Badge> : null}{' '}
            {Money.parse(cxc.vencido).esPositivo() ? <Badge variant="critico" data-testid="cliente-vencido">Deuda vencida $ {Money.parse(cxc.vencido).toDisplay()}: no se le vende sin autorización</Badge> : null}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader><CardDescription>Lista de precios</CardDescription><CardTitle>{c.lista_nombre}</CardTitle></CardHeader>
              <CardContent className="text-xs text-muted-foreground">{puedeCredito ? 'Solo administración cambia la lista.' : <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Solo administración</span>}</CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>Crédito</CardDescription>
                <CardTitle>{c.condicion === 'credito' ? <Dinero valor={c.limite_credito_usd} modo="usd" alineado="izquierda" /> : 'Contado'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {c.condicion === 'credito' ? (
                  <>
                    <p>Plazo: <b>{c.plazo_dias} días</b></p>
                    <p>CxC: <b className="num">$ {Money.parse(cxc.total).toDisplay()}</b> {Money.parse(cxc.vencido).esPositivo() ? <Badge variant="critico">vencido $ {Money.parse(cxc.vencido).toDisplay()}</Badge> : null}</p>
                    <p>Disponible: <b className="num text-mg-verde">$ {Money.parse(credito_disponible).toDisplay()}</b></p>
                  </>
                ) : (
                  <p className="text-muted-foreground">Sin límite de crédito asignado.</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>Saldo a favor</CardDescription>
                <CardTitle className="text-mg-verde"><Dinero valor={c.saldo_a_favor} modo="usd" alineado="izquierda" /></CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-muted-foreground">
                {veParte && c.saldo_divisa_sin_igtf ? (
                  <div className="rounded-lg bg-muted/60 px-2.5 py-2 text-foreground" data-testid="saldo-divisa-sin-igtf">
                    <p className="flex items-baseline justify-between gap-2 text-sm">En divisas sin IGTF <b><Dinero valor={c.saldo_divisa_sin_igtf} modo="usd" /></b></p>
                    <p className="text-[11px] text-muted-foreground">Entró en divisas y todavía no pagó el IGTF: paga el 3 % cuando se usa en una factura o una nota de débito. Al usar el saldo se gasta primero esta parte.</p>
                  </div>
                ) : null}
                <p>Solo lo mueven abonos, devoluciones, reintegros y su uso como pago, y el traspaso o el ajuste que autoriza la dueña. No se escribe a mano.</p>
                {puedeEditar && !c.consumidor_final && Money.parse(c.saldo_a_favor).esPositivo() ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOperacion('traspaso')} data-testid="btn-traspasar-saldo">Traspasar saldo</Button>
                    <Button size="sm" variant="outline" onClick={() => setOperacion('ajuste')} data-testid="btn-ajustar-saldo">Ajustar saldo</Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Movimientos de saldo</CardTitle>
              <CardDescription>Cada movimiento referencia su documento origen.</CardDescription>
            </CardHeader>
            <CardContent>
              {movimientos_saldo.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin movimientos.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                      {veParte ? <TableHead className="text-right" title="Lo que el movimiento sumó o restó a la parte del saldo en divisas sin IGTF, y cómo quedó">En divisas sin IGTF</TableHead> : null}
                      <TableHead>Usuario</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movimientos_saldo.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{formatearFechaHora(m.ocurrido_en)}</TableCell>
                        <TableCell><Badge variant={BAJA_SALDO.has(m.tipo) ? 'neutro' : 'ok'} data-testid="saldo-mov-tipo">{TIPO_MOVIMIENTO_SALDO[m.tipo] ?? m.tipo}</Badge></TableCell>
                        <TableCell className="text-xs" data-testid="saldo-mov-referencia">
                          {/* El recibo del abono y la nota o factura del movimiento se abren desde aquí (pedido de la dueña del 22-10) */}
                          {m.referencia_tipo === 'ABONO' ? (
                            <a href={`/imprimir/ABONO/${m.referencia_id}`} target="_blank" rel="noreferrer" className="underline" title="Ver el recibo del abono">{referenciaSaldo(m)}</a>
                          ) : m.documento_numero ? (
                            enlaceDocumento(m.referencia_id, referenciaSaldo(m), m.sucursal_nombre ?? '')
                          ) : (
                            referenciaSaldo(m)
                          )}
                        </TableCell>
                        <TableCell className="text-right"><Dinero valor={m.monto} modo="usd" /></TableCell>
                        <TableCell className="text-right"><Dinero valor={m.saldo_despues} modo="usd" /></TableCell>
                        {veParte ? (
                          <TableCell className="text-right" data-testid="saldo-mov-divisa">
                            {m.divisa_sin_igtf && m.divisa_sin_igtf_despues ? (
                              <>
                                {Money.parse(m.divisa_sin_igtf).esCero() ? <span className="text-muted-foreground">sin cambio</span> : <Dinero valor={m.divisa_sin_igtf} modo="usd" />}
                                <span className="block text-[10.5px] text-muted-foreground">queda <Dinero valor={m.divisa_sin_igtf_despues} modo="usd" className="inline" /></span>
                              </>
                            ) : '—'}
                          </TableCell>
                        ) : null}
                        <TableCell>{m.usuario_nombre ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          {cxc.documentos.length > 0 ? (
            <Card className="mt-4">
              <CardHeader><CardTitle>Cuentas por cobrar</CardTitle><CardDescription>Documentos a crédito con saldo, del más antiguo al más reciente.</CardDescription></CardHeader>
              <CardContent>
                <Table data-testid="cliente-cxc">
                  <TableHeader><TableRow><TableHead>Documento</TableHead><TableHead>Emitido</TableHead><TableHead>Vence</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {cxc.documentos.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{enlaceDocumento(d.id, `${d.tipo} ${d.numero_formateado}`, d.sucursal_nombre)}</TableCell>
                        <TableCell className="text-xs">{formatearFechaHora(d.emitido_en, { hour: undefined, minute: undefined })}</TableCell>
                        <TableCell className="text-xs">{d.vence_en ? formatearFechaHora(d.vence_en, { hour: undefined, minute: undefined }) : '—'}</TableCell>
                        <TableCell className="text-right num">$ {Money.parse(d.total).toDisplay()}</TableCell>
                        <TableCell className="text-right num">$ {Money.parse(d.saldo_pendiente).toDisplay()}</TableCell>
                        <TableCell>{d.dias_vencido > 0 ? <Badge variant="critico">{d.dias_vencido} día(s) de atraso</Badge> : <Badge variant="ok">al día</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Expediente</CardTitle>
              <CardDescription>Últimas compras, pagos, cotizaciones y abonos del cliente, en todas las sucursales.</CardDescription>
              {(() => {
                const otras = [...new Set([...documentos, ...cxc.documentos, ...pagos.filter((p) => p.documento_id)].map((x) => x.sucursal_nombre).filter((n) => !abre(n)))];
                return otras.length > 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="expediente-otra-sucursal">
                    Los documentos de {otras.join(' y ')} se listan sin enlace: {usuario?.rol === 'superadmin' ? 'para abrirlos, cambia a esa sucursal en la cabecera' : 'se abren desde esa sucursal'}.
                  </p>
                ) : null;
              })()}
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-3" data-testid="cliente-expediente">
              <div className="lg:col-span-2">
                <p className="mb-1 text-sm font-medium">Documentos</p>
                {documentos.length === 0 ? <p className="text-sm text-muted-foreground">Sin documentos.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Documento</TableHead><TableHead>Fecha</TableHead><TableHead>Sucursal</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {documentos.map((d) => (
                        <TableRow key={d.id} className={d.estado === 'anulado' ? 'text-muted-foreground' : ''}>
                          <TableCell>{enlaceDocumento(d.id, `${d.tipo} ${d.numero_formateado}`, d.sucursal_nombre)}{d.estado === 'anulado' ? <Badge variant="critico" className="ml-1">ANULADA</Badge> : null}</TableCell>
                          <TableCell className="text-xs">{formatearFechaHora(d.emitido_en)}</TableCell>
                          <TableCell className="text-xs">{d.sucursal_nombre}</TableCell>
                          <TableCell className="text-right num">$ {Money.parse(d.total).toDisplay()}</TableCell>
                          <TableCell className="text-right num">{Money.parse(d.saldo_pendiente).esPositivo() ? `$ ${Money.parse(d.saldo_pendiente).toDisplay()}` : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <p className="mb-1 mt-4 text-sm font-medium">Pagos recibidos</p>
                {pagos.length === 0 ? <p className="text-sm text-muted-foreground">Sin pagos.</p> : (
                  <Table data-testid="cliente-pagos">
                    <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Pago</TableHead><TableHead>Forma de pago</TableHead><TableHead>Sucursal</TableHead><TableHead className="text-right">Monto</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {pagos.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-xs">{formatearFechaHora(p.recibido_en)}</TableCell>
                          <TableCell>
                            {p.tipo === 'abono' ? <span className="inline-flex items-center gap-2"><a href={`/imprimir/ABONO/${p.id}`} target="_blank" rel="noreferrer" className="font-medium underline">Abono {p.numero_formateado ?? ''}</a><EnviarCorreo tipo="abono" id={p.id} enFila /></span> : p.documento_id ? enlaceDocumento(p.documento_id, `${p.tipo === 'reintegro' ? 'Reintegro de ' : ''}${p.documento ?? ''}`, p.sucursal_nombre) : <span>{p.tipo === 'reintegro' ? 'Reintegro' : 'Cobro'}</span>}
                          </TableCell>
                          <TableCell className="text-xs">{p.metodos.map((m) => METODO_PAGO_ETIQUETA[m as MetodoPago] ?? m).join(' + ') || '—'}</TableCell>
                          <TableCell className="text-xs">{p.sucursal_nombre}</TableCell>
                          <TableCell className="text-right num">$ {Money.parse(p.total_usd).toDisplay()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="mb-1 font-medium">Cotizaciones</p>
                  {cotizaciones.length === 0 ? <p className="text-muted-foreground">Sin cotizaciones.</p> : (
                    <ul className="space-y-0.5">{cotizaciones.map((q) => <li key={q.id} className="flex justify-between gap-2"><Link to={`/cotizaciones/${q.id}`} className="underline">{q.numero_formateado}</Link><span className="num text-muted-foreground">$ {Money.parse(q.total).toDisplay()} · {q.estado}</span></li>)}</ul>
                  )}
                </div>
                <div>
                  <p className="mb-1 font-medium">Abonos (adelantos)</p>
                  {abonos.length === 0 ? <p className="text-muted-foreground">Sin abonos.</p> : (
                    <ul className="space-y-0.5">{abonos.map((a) => <li key={a.id} className="flex justify-between gap-2"><a href={`/imprimir/ABONO/${a.id}`} target="_blank" rel="noreferrer" className="underline" title="Imprimir el recibo" data-testid={`abono-recibo-${a.numero_formateado ?? a.id}`}>{a.numero_formateado ?? 'Abono'}</a><span className="num text-muted-foreground">$ {Money.parse(a.total_usd).toDisplay()} · {formatearFechaHora(a.recibido_en, { hour: undefined, minute: undefined })}</span></li>)}</ul>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Archivos del cliente (RIF, cédula, cartas, comprobantes): pedido de la dueña 2026-10-19 */}
          <Card className="mt-4">
            <CardContent className="pt-5">
              <Adjuntos entidad="cliente" entidadId={c.id} titulo="Archivos del cliente" vacio="Sin archivos: adjunta la foto del RIF, la cédula o lo que haga falta." />
            </CardContent>
          </Card>
          {/* Registro de correos del cliente, de todas las sucursales (Fase 1 de retiros, 2026-10-29) */}
          <Card className="mt-4">
            <CardContent className="pt-5">
              <CorreosEnviados clienteId={c.id} conReferencia />
            </CardContent>
          </Card>
          <DialogClienteRapido abierto={editando} cerrar={() => setEditando(false)} cliente={c} />
          <Dialog open={!!credito} onOpenChange={(o) => !o && setCredito(null)}>
            <DialogContent>
              {credito ? (
                <div className="space-y-4" data-testid="form-credito">
                  <DialogHeader>
                    <div className="flex items-center gap-1"><DialogTitle>Lista, clasificación y crédito</DialogTitle><AyudaVentana tema="clientes-ficha.credito" /></div>
                    <DialogDescription>Exclusivo de administración (spec 03). Queda auditado.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <Campo etiqueta="Lista de precios" htmlFor="cr-lista">
                      <Select id="cr-lista" value={credito.lista_id} onChange={(e) => setCredito({ ...credito, lista_id: e.target.value })} data-testid="cr-lista">
                        {/* La lista del cliente puede estar desactivada: se muestra tal cual para que se vea que hay que cambiarla
                            (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H3) */}
                        {listas.data && !listas.data.some((l) => l.id === c.lista_id) ? <option value={c.lista_id} disabled>{c.lista_nombre} (desactivada: elige otra)</option> : null}
                        {(listas.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                      </Select>
                    </Campo>
                    <Campo etiqueta="Contribuyente" htmlFor="cr-tipo">
                      <Select id="cr-tipo" value={credito.tipo} onChange={(e) => setCredito({ ...credito, tipo: e.target.value as 'ordinario' | 'especial' })}>
                        <option value="ordinario">Ordinario</option>
                        <option value="especial">Especial (retiene IVA 75%)</option>
                      </Select>
                    </Campo>
                    <label className="flex h-10 items-center gap-2 text-sm">
                      <input type="checkbox" checked={credito.municipal} onChange={(e) => setCredito({ ...credito, municipal: e.target.checked })} /> Contribuyente municipal
                    </label>
                    {credito.municipal ? (
                      <Campo etiqueta="% municipal (vacío = global)" htmlFor="cr-pct">
                        <Input id="cr-pct" inputMode="decimal" value={credito.pct} onChange={(e) => setCredito({ ...credito, pct: e.target.value })} />
                      </Campo>
                    ) : <span />}
                    <Campo etiqueta="Condición" htmlFor="cr-cond">
                      <Select id="cr-cond" value={credito.condicion} onChange={(e) => setCredito({ ...credito, condicion: e.target.value as 'contado' | 'credito' })}>
                        <option value="contado">Contado</option>
                        <option value="credito">Crédito</option>
                      </Select>
                    </Campo>
                    {credito.condicion === 'credito' ? (
                      <>
                        <Campo etiqueta="Límite ($)" htmlFor="cr-limite">
                          <Input id="cr-limite" inputMode="decimal" value={credito.limite} onChange={(e) => setCredito({ ...credito, limite: e.target.value })} data-testid="input-limite" />
                        </Campo>
                        <Campo etiqueta={`Plazo en días (lo normal son ${PLAZO_CREDITO_NORMAL})`} htmlFor="cr-plazo">
                          <Input id="cr-plazo" inputMode="numeric" value={credito.plazo} onChange={(e) => setCredito({ ...credito, plazo: e.target.value.replace(/\D/g, '').slice(0, 3) })} data-testid="input-plazo" />
                        </Campo>
                        {Number(credito.plazo) > PLAZO_CREDITO_NORMAL ? <p className="col-span-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="aviso-plazo-largo">Plazo mayor al normal de {PLAZO_CREDITO_NORMAL} días: queda registrado y la dueña recibe el aviso.</p> : null}
                      </>
                    ) : null}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCredito(null)}>Cancelar</Button>
                    <Button onClick={() => void guardarCredito()} cargando={editarCredito.isPending} data-testid="guardar-credito">Guardar</Button>
                  </DialogFooter>
                </div>
              ) : null}
            </DialogContent>
          </Dialog>
        </>
      )}
    </EstadoQuery>
  );
}
